import React from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { fetchData } from '../utils/fetchData'
import { isValidField } from '../helpers/validation'
import Page from '../components/layout/Page'
import Content from '../components/layout/Content'
import ImageUpload from '../components/ImageUpload'
import Modal from '../components/Modal'
import Tags from '../components/Tags'
import Input from '../components/Input'
import Button from '../components/Button'
import Arrow from '../icons/Arrow'
import Loader from '../icons/Loader'
import ProtectedRoute from '../helpers/ProtectedRoute'
import styles from '../styles/Upload.module.css'
import { Media } from '../types/User'
import Gdpr from '../components/Gdpr'
import { WarningIcon } from '../icons/Warning'
import { InitialMetaData, MetaData } from '../types/MetaData'
import { mapPersonkategori } from '../utils/mapPersonKategori'
import { isValidDate } from '../utils/isValidDate'
import Banner from '../components/Banner'
import Checkbox from '../components/Checkbox'
import OptimizedImage from '../components/OptimizedImage'

function Upload () {
  const STORAGE_KEY = 'media'
  const localStorageMedia = localStorage.getItem(STORAGE_KEY)
  let media: Media | null = null
  if (localStorageMedia) {
    try {
      media = JSON.parse(localStorageMedia)
    } catch (error) {
      console.log(error)
    }
  }
  const token = media?.token ?? null
  const { storeId, channelType } = useParams()
  const navigate = useNavigate()
  const [modalIsActive, setModalIsActive] = React.useState<boolean>(false)
  const [under18ModalIsActive, setUnder18ModalIsActive] = React.useState<boolean>(false)
  const [preview, setPreview] = React.useState<string>('')
  const [buffer, setBuffer] = React.useState<Blob | null>(null)
  const [imageName, setImageName] = React.useState<string>('')
  const [validImageName, setValidImageName] = React.useState<string>('')
  const [imageNameErrorText, setImageNameErrorText] = React.useState<string>('')
  const [gdprErrorList, setGdprErrorList] = React.useState<{ errorType: string; errorText: string }[]>([])
  const [imgSrc, setImgSrc] = React.useState<string>('')
  const [imageFileName, setImageFileName] = React.useState<string>('')
  const [saving, setSaving] = React.useState<boolean>(false)
  const [tags, setTags] = React.useState<string[]>([])
  const [gdpr, setGdpr] = React.useState<boolean>(false)
  const [under18Consent, setUnder18Consent] = React.useState<boolean>(false)
  const [metaData, setMetaData] = React.useState<MetaData>({
    avtalsId: [],
    fotoDatum: '',
    personkategori: [],
    raderingsDatum: '',
  })
  
  function onModalClose () {
    setModalIsActive(false)
    setPreview('')
    setImageName('')
    setGdpr(false)
    setMetaData({
      fotoDatum: '',
      raderingsDatum: '',
      personkategori: [],
      avtalsId: [],
    })
  }

  function withoutSpecialChars () {
    const specialCharsFound = imageName.match(/[^A-Za-z0-9åäöÅÄÖ\s?]/g)
    if (specialCharsFound) {
      setImageNameErrorText(`Bildnamnet får inte innehålla några specialtecken ( ${specialCharsFound.join(' ')} )`)
      return false
    }
    return true
  }

  function validateStep () {
    const gdprErrors: { errorType: string; errorText: string }[] = []

    const isValidImageName = isValidField(imageName, setValidImageName) && withoutSpecialChars()

    if (isValidImageName !== true) {
      setValidImageName('ERROR')
      !imageNameErrorText && setImageNameErrorText('Bildnamn saknas')
    }

    if (gdpr) {    
      if (metaData.personkategori.length < 1 || (metaData.personkategori.includes('Minderårig') && metaData.personkategori.length < 2)) {
        gdprErrors.push({ errorType: 'personType', errorText: 'Välj typ av person i bilden' })
      }
        
      if (metaData.avtalsId.length < 1) {
        gdprErrors.push({ errorType: 'agreementIds', errorText: 'Fyll i referens / Avtals ID' })
      }
      
      if (metaData.personkategori.includes('Praktikant') || metaData.personkategori.includes('Privatperson')) {
        const photoDateRegex = /^\d{4}-\d{2}-\d{2}$/ // Regex for ÅÅÅÅ-MM-DD format
      
        if (!metaData.fotoDatum.trim()) {
          gdprErrors.push({ errorType: 'photoDate', errorText: 'Fyll i fotodatum' })
        } else if (!photoDateRegex.test(metaData.fotoDatum.trim()) || !isValidDate(metaData.fotoDatum.trim())) {
          gdprErrors.push({ errorType: 'photoDate', errorText: 'Fel format, ange datum enligt ÅÅÅÅ-MM-DD' })
        }
        if (!metaData.raderingsDatum.trim()) {
          gdprErrors.push({ errorType: 'photoDeletionDate', errorText: 'Fyll i raderingsdatum' })
        } else if (!photoDateRegex.test(metaData.raderingsDatum.trim()) || !isValidDate(metaData.raderingsDatum.trim())) {
          gdprErrors.push({ errorType: 'photoDeletionDate', errorText: 'Fel format, ange datum enligt ÅÅÅÅ-MM-DD' })
        }
      }
      setGdprErrorList(gdprErrors)

      if (gdprErrors.length === 0 && metaData.personkategori.includes('Minderårig')) {
        setUnder18ModalIsActive(true)
        return false
      }

    }
    return gdprErrors.length === 0 && isValidImageName === true
  }

  function arrayToString(arr: string[]) {
    const filteredArray = arr.filter(id => id !== '')
    if (filteredArray.length > 1) {
      return filteredArray.join(';')
    }
    return filteredArray[0] || ''
  }

  function onSave () {
    const isValid = validateStep()

    if (isValid) {
      onUpload()
    }
  }

  async function onUpload () {
    if (buffer && preview && token) {
      let config: { [key: string]: string } = {}
      setSaving(true)

      const formData = new FormData()
      formData.append('file', buffer, imageFileName)
      channelType && formData.append('tags', channelType)
      for (let i = 0; i < tags.length; i++) {
        formData.append('tags', tags[i])
      }
      formData.append('imageName', imageName)

      if (gdpr) {
        const updatedMetaData: InitialMetaData = {
          ...metaData,
          avtalsId: arrayToString(metaData.avtalsId),
          personkategori: mapPersonkategori(metaData.personkategori, false),
        }  
        formData.append('metadata', JSON.stringify(updatedMetaData))

        if (import.meta.env.VITE_ENV === 'production') {
          try {
            const response = await fetchData(`/api/configuration`, {
              method: "GET",
            })
    
            if (response) {
              const { data } = response
              config = data
            }
          } catch (error) {
            console.log(error)
          }
        }
      }

      try {
        const res = await fetchData(`/api/cloudinary/store/${storeId}/channel/${channelType}/images`, {
          method: "POST",
          headers: config,
          body: formData
        })

        if (res) {
          const { data, status } = res
          if (status === 200 && data && data.asset_id) {
            setSaving(false)
            onModalClose()
            setImgSrc('')
            navigate(`/${storeId}/${channelType}/${data?.asset_id}`)
          }
        }
      } catch (error) {
        console.log(error)
      }
      setSaving(false)
      onModalClose()
      setImgSrc('')
    }
  }

  return (
    <ProtectedRoute user={token}>
      <Page>
        <div className={styles.upload}>
          <Content>
            <Link
              className={styles.backButton}
              to={`/${storeId}/${channelType}`}
            >
              <Arrow className={styles.arrowIcon} />
              <span>Till bildgalleriet</span>
            </Link>
            <h1 className={styles.heading}>Ladda upp ny bild</h1>
            <div className={styles.content}>
              <ImageUpload
                setPreview={setPreview}
                setBuffer={setBuffer}
                modalIsActive={modalIsActive}
                setModalIsActive={setModalIsActive}
                imgSrc={imgSrc}
                setImgSrc={setImgSrc}
                setImageFileName={setImageFileName}
              />
            </div>
            <Modal
              onCancel={onModalClose}
              active={modalIsActive}
              maxWidth="78rem"
              title="Ladda upp bild"
              height="100%"
            >
              <div className={styles.modalContent}>
                <div className={styles.preview}>
                  <OptimizedImage
                    className={styles.image}
                    src={preview}
                    alt="Förhandsgranskning"
                  />
                </div>
                <div className={styles.form}>
                  <div className={styles.inputContainer}>
                    <h3 className={styles.modalHeading}>Namn*</h3>
                    <Input
                      id="imageName"
                      type="text"
                      placeholder="Ex. “Smörgåstårta med räkor”"
                      value={imageName}
                      onChange={(e) => { setImageName(e.target.value); setValidImageName('IDLE') }}
                      error={validImageName === 'ERROR'}
                      errorText={imageNameErrorText}
                      errorIcon={validImageName === 'ERROR' && <WarningIcon color={'var(--redMid)'} className={styles.errorIcon} />}
                    />
                    <label className={styles.label} htmlFor="imageName">
                      Ge bilden ett beskrivande namn
                    </label>
                  </div>
                  <div className={styles.inputContainer}>
                    <h3 className={styles.modalHeading} style={{marginBottom: tags.length > 0 ? '0.5rem' : '0'}}>
                      Tagg
                    </h3>
                    <Tags tags={tags} setTags={setTags} />
                  </div>
                  <Gdpr 
                    gdpr={gdpr} 
                    setGdpr={setGdpr}
                    gdprData={metaData}
                    setGdprData={setMetaData}
                    gdprErrorList={gdprErrorList}
                    setGdprErrorList={setGdprErrorList}
                  />
                  <div className={styles.uploadAction}>
                    <Button size='large' color='redMid' className={styles.uploadButton} onClick={onSave}>
                      {saving ? (
                        <Loader className={styles.loader} />
                      ) : (
                        <span>Spara</span>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </Modal>
            <Modal
              onCancel={() => setUnder18ModalIsActive(false)}
              active={under18ModalIsActive}
              maxWidth="32rem"
              title="Observera"
              modalBodyPadding="7rem 3rem 3rem"
              height="100%"
            >
              <div className={styles.consentModalContent}>
                <Banner theme="info">
                  <div className={styles.banner}>
                    <p className={styles.bannerText}>
                      Skriftligt samtycke från vårdnadshavare krävs vid material på barn. 
                      Observera att uppladdning av bilder med identifierbara personer innebär behandling av personuppgifter 
                      och att det är ditt ansvar att följa dataskyddsförordningens regler. <a href="https://www.ica.se/dataskydd" className={styles.bannerLink} target="_blank" rel="noreferrer">Besök www.ica.se/dataskydd</a> för mer information.
                    </p>
                  </div>
                </Banner>
                <Checkbox 
                  label="Jag förstår att jag är ansvarig" 
                  id="under18Consent" 
                  checked={under18Consent}
                  onChange={() => setUnder18Consent(!under18Consent)} 
                />
                <div className={styles.consentButtonContainer}>
                  <Button disabled={!under18Consent} size="medium" onClick={() => {setUnder18ModalIsActive(false); onUpload()}}>
                    Spara
                  </Button>
                </div>
              </div>
            </Modal>
          </Content>
        </div>
      </Page>
    </ProtectedRoute>
  )
}

export default Upload
